import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser } from '../../types';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import TopMenu from './TopMenu';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';
import type { NavLayout } from '../../utils/churchSettings';

// The gradient page shell + sticky navbar + the navigation the parish chose
// under Settings › Appearance + content area.
//
//   tiles    top bar with "Back to Dashboard" and ⚙️; the dashboard's tiles are the menu
//   sidebar  top bar + a module menu down the left (a drawer on a narrow screen)
//   topmenu  top bar + a row of category menus beneath it
export interface AppLayoutProps {
  user: AuthUser | null;
  isAdmin?: boolean;
  layout?: NavLayout;
  /** On the dashboard. */
  atHome?: boolean;
  onHome?: () => void;
  onOpenSettings?: () => void;
  onLogout?: () => void;
  children?: ReactNode;
}

const AppLayout = ({
  user, isAdmin, layout = 'tiles', atHome = false, onHome, onOpenSettings, onLogout, children
}: AppLayoutProps) => {
  const { isDarkMode } = useTheme();
  const t = getTokens(isDarkMode);

  // The sidebar drawer on a narrow screen. Closed again whenever the layout
  // changes, so switching away from the sidebar cannot strand an open drawer.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [layout]);

  return (
    <div style={{
      minHeight: '100vh', width: '100vw', maxWidth: '100%',
      margin: 0, padding: 0,
      background: t.bgApp,
      display: 'flex', flexDirection: 'column',
      position: 'relative', zIndex: 1,
      transition: 'background 0.3s ease'
    }}>
      <Navbar
        user={user}
        isAdmin={isAdmin}
        atHome={atHome}
        showBack={layout === 'tiles' && !atHome}
        showSettings={layout === 'tiles'}
        showMenuButton={layout === 'sidebar'}
        onToggleMenu={() => setMenuOpen((open) => !open)}
        onHome={onHome}
        onOpenSettings={onOpenSettings}
        onLogout={onLogout}
      />

      {layout === 'topmenu' && <TopMenu user={user} />}

      <div style={{ display: 'flex', alignItems: 'stretch', flexGrow: 1, minWidth: 0 }}>
        {layout === 'sidebar' && (
          <Sidebar user={user} mobileOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        )}
        <div style={{
          padding: layout === 'tiles' ? t.spacing.xxl : t.spacing.lg,
          flexGrow: 1, minWidth: 0, width: '100%',
          boxSizing: 'border-box', position: 'relative', zIndex: 2
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
